import React, { PureComponent } from 'react'
import Constants from 'expo-constants'
import { View, Image, Platform } from 'react-native'
import styled from 'styled-components/native'

import { Text } from '../../Atoms/Typography'
import Touchable from '../../Atoms/Touchable'
import Icon from '../../Atoms/Icon'
import InlineError from '../../Atoms/InlineError'
import Button from '../../Atoms/Button'
import HeaderBar from '../../Molecules/HeaderBar'
import Spinner from '../../Atoms/Spinner'
import OptionsAlert from '../../Molecules/OptionsAlert'
import theme from '../../../styles/theme'

import dateFormat from 'dateformat'

const Container = styled(View)`
  flex: 1;
  background-color: ${theme.colours.black};
`

const Body = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.headerHeight}px;
`

const Logo = styled(Image)`
  width: 200px;
  height: 200px;
`

const FakeButtonHeight = styled(View)`
  height: 80px;
`

const LargerTouchable = styled(Touchable)`
  height: 100%;
  justify-content: center;
  padding: 20px 20px 20px 10px;
`

const ReconnectButton = styled(Button)`
  border: solid 1px ${theme.colours.white};
  border-radius: 5px;
  opacity: 0.5;
`

const Row = styled(View)`
  flex-direction: row;
`

const Footer = styled(View)`
  height: 60px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
`

const compareDates = datestring => {
  const today = new Date().toLocaleDateString()
  const event = new Date(datestring).toLocaleDateString()
  if (today === event) {
    return 0
  }
  if (today < event) {
    return -1
  }
  return 1
}

const getWelcomeMessage = (participantDetails, gameStarted, gameState) => {
  if (Platform.OS !== 'web' || !participantDetails) {
    return
  }
  let welcomeMessage = `Welcome ${participantDetails.firstName}`

  if (participantDetails.eventClosed) {
    return welcomeMessage + ', your event is now over'
  }
  const eventDate = new Date(participantDetails.eventDate)

  const result = compareDates(participantDetails.eventDate)
  if (result < 0) {
    welcomeMessage += `, your event will start on ${dateFormat(eventDate, 'ddd dd-mmm-yy')}`
  } else if (result === 0) {
    if (!gameStarted) {
      welcomeMessage += ', your event will start soon'
    } else if (gameState === 'locked') {
      welcomeMessage += ', your event will resume shortly'
    } else {
      welcomeMessage += ', your event is open'
    }
  } else {
    welcomeMessage += ', your event is now over'
  }

  return welcomeMessage
}

const shouldShowRegisterError = (registerError, participantDetails) => {
  if (!registerError) {
    return false
  }
  if (Platform.OS === 'web' && registerError.code === 403) {
    return false
  }
  if (Platform.OS === 'web' && registerError.code === 422 && registerError.message === 'The game has not been setup yet') {
    return false
  }
  if (Platform.OS === 'web' && registerError.message === 'Server not available' && participantDetails && participantDetails.eventClosed) {
    return false
  }
  return true
}

export default class SplashScreen extends PureComponent {
  state = {
    showGameSelectAlert: false
  }

  handleCloseGameSelectAlert = () => {
    this.setState({ showGameSelectAlert: false })
  }

  handleSelectGame = (gameId) => {
    this.handleCloseGameSelectAlert()
    this.props.onAttemptReconnect(gameId)
  }

  handleAttemptReconnect = () => {
    if (this.props.games && this.props.games.length === 1) {
      return this.handleSelectGame(this.props.games[0].gameId)
    }
    if (!this.props.games || !this.props.games.length) {
      this.props.onFetchGames()
    }
    this.setState({ showGameSelectAlert: true })
  }

  render () {
    const { games, gameState, gameStarted, registerError, teamName, downloading, onButtonPress, onSettingsPress, buttonEnabled, allowReconnect, gameName, splashScreenTitle, hasCustomLogo, customLogoFile, participantDetails } = this.props

    let source = require('../../../assets/logo.png')

    if (hasCustomLogo && customLogoFile) {
      source = { uri: customLogoFile }
    }

    const welcomeMessage = getWelcomeMessage(participantDetails, gameStarted, gameState)

    const showError = shouldShowRegisterError(registerError, participantDetails)

    return (
      <Container>
        <HeaderBar black>
          <LargerTouchable onPress={onSettingsPress}>
            <Icon name='cog' white size='huge' />
          </LargerTouchable>
          {allowReconnect && (
            <ReconnectButton onPress={this.handleAttemptReconnect}>
              <Text size='m' white>RECONNECT</Text>
            </ReconnectButton>
          )}
          {showError && (
            <Row style={{ marginLeft: theme.gutterUnit }}>
              <InlineError message={registerError.message} />
            </Row>
          )}
          {downloading && (
            <Row>
              <Spinner white />
              <Text overrides={{ marginLeft: theme.gutterUnit }} white>Downloading files...</Text>
            </Row>
          )}
          {teamName && <Text white overrides={{ marginLeft: theme.gutterUnit }}>{teamName || ''}</Text>}
        </HeaderBar>
        <Body>
          {games && games.length && (
            <OptionsAlert
              visible={this.state.showGameSelectAlert}
              onCancel={this.handleCloseGameSelectAlert}
              title='Select game to connect'
              options={games.map(g => {
                return {
                  value: g.gameId,
                  name: `${g.splashScreenTitle} - ${g.gameName}`,
                  handlePress: () => this.handleSelectGame(g.gameId),
                  selected: false
                }
              })}
            />
          )}
          <Logo source={source} />
          <Text fontSize={100} white proxima overrides={{ marginBottom: theme.gutterUnit }}>{splashScreenTitle || 'ASCENSION'}</Text>
          {!!welcomeMessage && (
            <Text size='xxxl' proxima white overrides={{ marginBottom: theme.gutterUnit }}>
              {welcomeMessage}
            </Text>
          )}
          {buttonEnabled ? (
            <Button
              secondary={buttonEnabled}
              darkgrey={!buttonEnabled}
              rounded
              onPress={onButtonPress}
              disabled={!buttonEnabled}
            >
              <Text size='xxl' bold white={!buttonEnabled}>LET'S GO!</Text>
            </Button>
          ) : <FakeButtonHeight />}
        </Body>
        <Footer>
          <Text size='l' bold white>Version: {Constants.manifest.version}</Text>
          <Text size='l' bold white>{gameName ? `Game: ${gameName}` : ' '}</Text>
        </Footer>
      </Container>
    )
  }
}
